import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { getTenantId, getDepartmentId } from 'src/app/services/store-snapshot.service';
import { MainTableFilterModel } from 'src/app/shared/models/main-table-filter.model';
import { createFilterParams } from 'src/app/shared/functions/create-filter-params';
import {
  IApiResponseGroupSignedOffDocuments,
  ISignOffHistoryListAdditional,
  ISignOffNotification,
  ISignOffAdminRequest,
  ISignOffResponse,
  ISignOffSeafarerRequest,
  IDocumentsToSignOffResponse,
} from '../models/sign-off.model';
import { MainTableFilteringHelperService } from 'src/app/shared/main-table-module/services/main-table-filtering-helper.service';

const COLUMN_TO_KEY_MAP: any = {
  Name: 'UserFullName',
  signedOn: 'Created',
  signedBy: 'AdminSignedByName',
  IsActive: 'status',
};

@Injectable({
  providedIn: 'root',
})
export class SignOffApiService {
  headers = { 'content-type': 'application/json' };

  private get url(): string {
    return `${
      environment.apiUrl
    }/api/${getTenantId()}/${getDepartmentId()}/PlannedActivitySignOffPeriods`;
  }

  constructor(private http: HttpClient) {}

  getDocumentsToBeSignedOff(): Observable<IDocumentsToSignOffResponse> {
    return this.http.get<IDocumentsToSignOffResponse>(`${this.url}/DocumentsToSignOff`);
  }

  getSignOffNotification(): Observable<ISignOffNotification> {
    return this.http.get<ISignOffNotification>(`${this.url}/NotificationInformation`);
  }

  signOffDocumentAsAdmin(request: ISignOffAdminRequest): Observable<ISignOffResponse> {
    return this.http.post<ISignOffResponse>(`${this.url}/AdminSignOff`, request, {
      headers: this.headers,
    });
  }

  signOffDocumentAsSeafarer(request: ISignOffSeafarerRequest): Observable<ISignOffResponse> {
    return this.http.post<ISignOffResponse>(
      `${this.url}/${request.plannedActivitySignOffPeriodId}/SeafarerSignOff`,
      request,
      {
        headers: this.headers,
      }
    );
  }

  rejectDocumentAsSeafarer(
    plannedActivitySignOffPeriodId: string,
    comment: string
  ): Observable<ISignOffResponse> {
    return this.http.post<ISignOffResponse>(
      `${this.url}/${plannedActivitySignOffPeriodId}/Reject`,
      { comment },
      {
        headers: this.headers,
      }
    );
  }

  reopenDocument(
    plannedActivitySignOffPeriodId: string,
    comment: string
  ): Observable<ISignOffResponse> {
    return this.http.post<ISignOffResponse>(
      `${this.url}/${plannedActivitySignOffPeriodId}/Reopen`,
      { comment },
      {
        headers: this.headers,
      }
    );
  }

  closeDocument(
    plannedActivitySignOffPeriodId: string,
    comment: string
  ): Observable<ISignOffResponse> {
    return this.http.post<ISignOffResponse>(
      `${this.url}/${plannedActivitySignOffPeriodId}/Close`,
      { comment },
      {
        headers: this.headers,
      }
    );
  }

  remindSeafarer(
    plannedActivitySignOffPeriodId: string,
    message: string
  ): Observable<ISignOffResponse> {
    return this.http.post<ISignOffResponse>(
      `${this.url}/${plannedActivitySignOffPeriodId}/RemindSeafarer`,
      { message },
      {
        headers: this.headers,
      }
    );
  }

  getSignedOffDocuments(
    currentFilter?: MainTableFilterModel
  ): Observable<IApiResponseGroupSignedOffDocuments> {
    let params: HttpParams = createFilterParams(currentFilter, undefined, COLUMN_TO_KEY_MAP);
    const addl = currentFilter?.additional as ISignOffHistoryListAdditional | undefined;
    if (addl) {
      if (addl.year !== undefined && addl.month !== undefined) {
        params = params.set('Year', `${addl.year}`);
        params = params.set('Month', `${addl.month}`);
      }
      if (addl.tripId) {
        params = params.set('TripId', addl.tripId);
      }
      if (addl.statuses?.length) {
        for (const statusId of addl.statuses) {
          params = params.append('Statuses', `${statusId}`);
        }
      }
    }
    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      currentFilter,
      COLUMN_TO_KEY_MAP
    );

    return this.http.get<IApiResponseGroupSignedOffDocuments>(this.url, { params });
  }

  getSignedOffDocumentById(id: string): Observable<ISignOffResponse> {
    return this.http.get<ISignOffResponse>(`${this.url}/${id}`);
  }

  printSignedOffDocumentById(id: string): Observable<Blob> {
    return this.http.get<Blob>(`${this.url}/${id}/print/pdf`, { responseType: 'blob' as 'json' });
  }
}
